import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Mail, Phone, User, Lock, Car, Upload, FileText, MapPin, Camera } from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login, register, driver_register, verify, forgot, reset
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Email verification fields
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingVerificationPhone, setPendingVerificationPhone] = useState("");
  const [resendingCode, setResendingCode] = useState(false);
  
  // Forgot password fields
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotPhone, setForgotPhone] = useState("");
  
  // Driver registration fields
  const [address, setAddress] = useState("");
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [numCars, setNumCars] = useState("1");
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [rcFile, setRcFile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  
  const aadhaarRef = useRef(null);
  const licenseRef = useRef(null);
  const rcRef = useRef(null);
  const profileRef = useRef(null);
  
  const navigate = useNavigate();
  const { user, login } = useAuth();

  // Read referral code from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) { setReferralCode(ref); setMode("register"); }
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/super-admin", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "driver") navigate("/driver", { replace: true });
      else navigate("/customer", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Handle driver registration separately
      if (mode === "driver_register") {
        // First upload documents (use public upload - driver not authenticated yet)
        const uploads = {};
        const failedUploads = [];
        
        const uploadDoc = async (file, key, label) => {
          if (!file) return;
          const formData = new FormData();
          formData.append("file", file);
          try {
            const uploadRes = await safeFetch(`${API}/upload/public`, { method: "POST", body: formData });
            if (uploadRes.ok) {
              const data = await uploadRes.json();
              uploads[key] = data.url;
            } else {
              failedUploads.push(label);
            }
          } catch (err) {
            failedUploads.push(label);
          }
        };
        
        await uploadDoc(aadhaarFile, "aadhaar", "Aadhaar");
        await uploadDoc(licenseFile, "license", "License");
        await uploadDoc(rcFile, "rc", "Vehicle RC");
        await uploadDoc(profilePhoto, "profile_photo", "Profile Photo");
        
        if (failedUploads.length > 0) {
          toast.error(`Failed to upload: ${failedUploads.join(", ")}. Please retry.`);
          setLoading(false);
          return;
        }
        
        // Register as driver
        const res = await safeFetch(`${API}/auth/register-driver`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            phone,
            email: email || null,
            password,
            address,
            aadhaar_number: aadhaarNumber,
            license_number: licenseNumber,
            vehicle_number: vehicleNumber,
            vehicle_type: vehicleType,
            num_cars: parseInt(numCars) || 1,
            documents: uploads
          }),
        });
        
        const data = await res.json();
        if (!res.ok) {
          toast.error(data.detail || "Registration failed");
          return;
        }
        
        toast.success("Driver registration submitted! Please wait for admin approval.");
        setMode("login");
        return;
      }
      
      // Regular login/register
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      
      // Validate email for registration
      if (mode === "register" && !email) {
        toast.error("Email is required for registration");
        setLoading(false);
        return;
      }
      
      // Build request body - exclude null/empty optional fields
      let body;
      if (mode === "login") {
        body = { phone, password };
      } else {
        body = { name, phone, email, password, role: "customer" };
        if (referralCode && referralCode.trim()) {
          body.referral_code = referralCode.trim();
        }
      }
      
      // Use XMLHttpRequest to avoid conflicts with Emergent's fetch interceptor
      const responseData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          } catch (e) {
            reject(new Error("Failed to parse response"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network request failed"));
        };
        
        xhr.send(JSON.stringify(body));
      });

      if (!responseData.ok) {
        const data = responseData.data;
        // Handle email verification required
        if (responseData.status === 403 && data.detail && data.detail.includes("verify your email")) {
          toast.error("Please verify your email first");
          setPendingVerificationPhone(phone);
          setMode("verify");
          return;
        }
        // Handle validation errors from Pydantic
        if (data.detail && Array.isArray(data.detail)) {
          const errorMsg = data.detail.map(e => e.msg).join(', ');
          toast.error(errorMsg || "Validation error");
        } else {
          toast.error(data.detail || "Something went wrong");
        }
        return;
      }

      const data = responseData.data;
      
      // After registration, show verification screen
      if (mode === "register") {
        toast.success("Account created! Please verify your email.");
        setPendingVerificationPhone(phone);
        setMode("verify");
        return;
      }
      
      // Login successful
      login(data.token, data.user);
      toast.success("Welcome back!");
    } catch (err) {
      console.error("Auth error:", err);
      toast.error(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle email verification
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: pendingVerificationPhone, code: verificationCode }),
      });
      
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Invalid verification code");
        return;
      }
      
      toast.success("Email verified! You can now login.");
      setMode("login");
      setVerificationCode("");
    } catch (err) {
      toast.error("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification code
  const handleResendCode = async () => {
    if (resendingCode) return;
    
    setResendingCode(true);
    try {
      const res = await safeFetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: pendingVerificationPhone }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success("New verification code sent to your email!");
      } else {
        toast.error(data.detail || "Failed to resend code");
      }
    } catch (err) {
      toast.error("Failed to resend code");
    } finally {
      setResendingCode(false);
    }
  };
  
  // Handle forgot password - request reset code
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPhone) {
      toast.error("Please enter your phone number");
      return;
    }
    
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: forgotPhone }),
      });
      
      const data = await res.json();
      toast.success("If an account exists, a reset code has been sent to your email");
      setMode("reset");
    } catch (err) {
      toast.error("Failed to send reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode || resetCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          phone: forgotPhone, 
          code: resetCode, 
          new_password: newPassword 
        }),
      });
      
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Failed to reset password");
        return;
      }
      
      toast.success("Password reset successfully! You can now login.");
      setMode("login");
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
      setForgotPhone("");
    } catch (err) {
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1666545385170-1dfae4d93605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxrYXNobWlyJTIwbW91bnRhaW4lMjByb2FkJTIwc2NlbmljJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MTI0MTM2MXww&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/40 to-forest/30" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          <img src={LOGO_URL} alt="Cab Valley" className="h-14 w-auto logo-light self-start" />
          <div>
            <h2 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
              Your Valley.{" "}
              <span className="text-gradient-gold">Your Ride.</span>
            </h2>
            <p className="mt-4 text-white/60 text-lg leading-relaxed">
              Reliable mobility across Jammu, Kashmir & Ladakh.
            </p>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <button
            data-testid="auth-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-forest/60 hover:text-forest text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>

          <div className="mb-8">
            <img src={LOGO_URL} alt="Cab Valley" className="h-10 w-auto mb-8" />
            <h1 className="font-heading text-3xl font-bold text-forest">
              {mode === "login" ? "Welcome back" : 
               mode === "driver_register" ? "Join as Driver" : 
               mode === "verify" ? "Verify Email" : 
               mode === "forgot" ? "Forgot Password" :
               mode === "reset" ? "Reset Password" :
               "Create account"}
            </h1>
            <p className="text-forest/45 mt-2 text-[15px]">
              {mode === "login" ? "Sign in to continue your journey" : 
               mode === "driver_register" ? "Register to start earning with Cab Valley" : 
               mode === "verify" ? "Enter the 6-digit code sent to your email" : 
               mode === "forgot" ? "Enter your phone number to receive a reset code" :
               mode === "reset" ? "Enter the code sent to your email and set a new password" :
               "Get started with Cab Valley"}
            </p>
          </div>

          {/* Forgot Password Form */}
          {mode === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <Label className="text-forest/70 text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone Number
                </Label>
                <Input
                  type="tel"
                  value={forgotPhone}
                  onChange={(e) => setForgotPhone(e.target.value)}
                  placeholder="Enter your registered phone number"
                  className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || !forgotPhone}
                className="w-full py-5 rounded-xl bg-forest hover:bg-forest/90 text-white font-semibold transition-all"
              >
                {loading ? "Sending..." : "Send Reset Code"}
              </Button>
              
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setForgotPhone("");
                }}
                className="w-full text-center text-forest/60 text-sm hover:text-forest"
              >
                Back to Login
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <Label className="text-forest/70 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Reset Code
                </Label>
                <Input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 text-center text-2xl tracking-widest font-mono focus:ring-2 focus:ring-forest focus:border-transparent"
                />
              </div>
              
              <div>
                <Label className="text-forest/70 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" /> New Password
                </Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                />
              </div>
              
              <div>
                <Label className="text-forest/70 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Confirm Password
                </Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || resetCode.length !== 6 || !newPassword || !confirmPassword}
                className="w-full py-5 rounded-xl bg-forest hover:bg-forest/90 text-white font-semibold transition-all"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
              
              <div className="text-center space-y-3">
                <p className="text-forest/50 text-sm">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-forest font-medium text-sm hover:underline"
                >
                  Request New Code
                </button>
              </div>
              
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setResetCode("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="w-full text-center text-forest/60 text-sm hover:text-forest"
              >
                Back to Login
              </button>
            </form>
          )}

          {/* Email Verification Form */}
          {mode === "verify" && (
            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <Label className="text-forest/70 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Verification Code
                </Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 text-center text-2xl tracking-widest font-mono focus:ring-2 focus:ring-forest focus:border-transparent"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full py-5 rounded-xl bg-forest hover:bg-forest/90 text-white font-semibold transition-all"
              >
                {loading ? "Verifying..." : "Verify Email"}
              </Button>
              
              <div className="text-center space-y-3">
                <p className="text-forest/50 text-sm">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  className="text-forest font-medium text-sm hover:underline disabled:opacity-50"
                >
                  {resendingCode ? "Sending..." : "Resend Code"}
                </button>
              </div>
              
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setVerificationCode("");
                }}
                className="w-full text-center text-forest/60 text-sm hover:text-forest"
              >
                Back to Login
              </button>
            </form>
          )}

          {/* Mode Tabs for Registration */}
          {(mode === "register" || mode === "driver_register") && (
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-xl">
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  mode === "register" ? "bg-white text-forest shadow-sm" : "text-forest/60 hover:text-forest"
                }`}
              >
                <User className="w-4 h-4 inline mr-1.5" /> Customer
              </button>
              <button
                type="button"
                data-testid="driver-signup-tab"
                onClick={() => setMode("driver_register")}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  mode === "driver_register" ? "bg-white text-forest shadow-sm" : "text-forest/60 hover:text-forest"
                }`}
              >
                <Car className="w-4 h-4 inline mr-1.5" /> Driver
              </button>
            </div>
          )}

          {/* Form - hidden during verification, forgot, and reset */}
          {mode !== "verify" && mode !== "forgot" && mode !== "reset" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Registration Fields */}
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-forest/70 text-sm flex items-center gap-2">
                    <User className="w-4 h-4" /> Full Name
                  </Label>
                  <Input
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                  />
                </div>
                <div>
                  <Label className="text-forest/70 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="register-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                  />
                  <p className="text-xs text-forest/40 mt-1">Required for booking confirmations & verification</p>
                </div>
                <div>
                  <Label className="text-forest/70 text-sm flex items-center gap-2">
                    Referral Code (Optional)
                  </Label>
                  <Input
                    data-testid="referral-code-input"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Enter referral code"
                    className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
                  />
                  {referralCode && <p className="text-xs text-green-600 mt-1">You & your friend both get {"\u20B9"}100!</p>}
                </div>
              </>
            )}

            {/* Driver Registration Fields */}
            {mode === "driver_register" && (
              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/70 text-sm"><User className="w-3 h-3 inline mr-1" /> Full Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                  <div>
                    <Label className="text-forest/70 text-sm"><Mail className="w-3 h-3 inline mr-1" /> Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                </div>
                <div>
                  <Label className="text-forest/70 text-sm"><MapPin className="w-3 h-3 inline mr-1" /> Address</Label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full address" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/70 text-sm"><FileText className="w-3 h-3 inline mr-1" /> Aadhaar Number</Label>
                    <Input value={aadhaarNumber} onChange={(e) => setAadhaarNumber(e.target.value)} placeholder="XXXX XXXX XXXX" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                  <div>
                    <Label className="text-forest/70 text-sm"><FileText className="w-3 h-3 inline mr-1" /> License Number</Label>
                    <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="DL-XXXXXXXXXX" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/70 text-sm"><Car className="w-3 h-3 inline mr-1" /> Vehicle Number</Label>
                    <Input value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} placeholder="JK01AB1234" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                  <div>
                    <Label className="text-forest/70 text-sm"><Car className="w-3 h-3 inline mr-1" /> Vehicle Type</Label>
                    <Input value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} placeholder="Swift Dzire, Innova" required className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                  </div>
                </div>
                <div>
                  <Label className="text-forest/70 text-sm"><Car className="w-3 h-3 inline mr-1" /> Number of Cars Owned</Label>
                  <Input type="number" min="1" value={numCars} onChange={(e) => setNumCars(e.target.value)} className="mt-1 rounded-xl border-gray-200 bg-gray-50/50 py-4" />
                </div>
                
                {/* Document Uploads */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm font-medium text-forest mb-3">Upload Documents</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input type="file" ref={aadhaarRef} accept="image/*,.pdf" onChange={(e) => setAadhaarFile(e.target.files[0])} className="hidden" />
                      <button type="button" onClick={() => aadhaarRef.current?.click()} className={`w-full p-3 rounded-xl border-2 border-dashed ${aadhaarFile ? "border-green-400 bg-green-50" : "border-gray-200"} text-sm hover:border-forest transition-colors`}>
                        <Upload className="w-4 h-4 mx-auto mb-1" />
                        {aadhaarFile ? aadhaarFile.name.slice(0, 15) + "..." : "Aadhaar Card"}
                      </button>
                    </div>
                    <div>
                      <input type="file" ref={licenseRef} accept="image/*,.pdf" onChange={(e) => setLicenseFile(e.target.files[0])} className="hidden" />
                      <button type="button" onClick={() => licenseRef.current?.click()} className={`w-full p-3 rounded-xl border-2 border-dashed ${licenseFile ? "border-green-400 bg-green-50" : "border-gray-200"} text-sm hover:border-forest transition-colors`}>
                        <Upload className="w-4 h-4 mx-auto mb-1" />
                        {licenseFile ? licenseFile.name.slice(0, 15) + "..." : "Driving License"}
                      </button>
                    </div>
                    <div>
                      <input type="file" ref={rcRef} accept="image/*,.pdf" onChange={(e) => setRcFile(e.target.files[0])} className="hidden" />
                      <button type="button" onClick={() => rcRef.current?.click()} className={`w-full p-3 rounded-xl border-2 border-dashed ${rcFile ? "border-green-400 bg-green-50" : "border-gray-200"} text-sm hover:border-forest transition-colors`}>
                        <Upload className="w-4 h-4 mx-auto mb-1" />
                        {rcFile ? rcFile.name.slice(0, 15) + "..." : "Vehicle RC"}
                      </button>
                    </div>
                    <div>
                      <input type="file" ref={profileRef} accept="image/*" onChange={(e) => setProfilePhoto(e.target.files[0])} className="hidden" />
                      <button type="button" onClick={() => profileRef.current?.click()} className={`w-full p-3 rounded-xl border-2 border-dashed ${profilePhoto ? "border-green-400 bg-green-50" : "border-gray-200"} text-sm hover:border-forest transition-colors`}>
                        <Camera className="w-4 h-4 mx-auto mb-1" />
                        {profilePhoto ? profilePhoto.name.slice(0, 15) + "..." : "Profile Photo"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <Label className="text-forest/70 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" /> Phone Number
              </Label>
              <Input
                data-testid="auth-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                required
                className="mt-1.5 rounded-xl border-gray-200 bg-gray-50/50 py-5 focus:ring-2 focus:ring-forest focus:border-transparent"
              />
            </div>
            <div>
              <Label className="text-forest/70 text-sm flex items-center gap-2">
                <Lock className="w-4 h-4" /> Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="auth-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="rounded-xl border-gray-200 bg-gray-50/50 py-5 pr-10 focus:ring-2 focus:ring-forest focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/40 hover:text-forest/60"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-sm text-amber hover:underline mt-2 block text-right w-full"
                >
                  Forgot Password?
                </button>
              )}
            </div>
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl py-5 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : mode === "driver_register" ? "Submit Application" : "Create Account"}
            </Button>
          </form>
          )}

          {mode !== "verify" && mode !== "forgot" && mode !== "reset" && (
          <p className="text-center text-sm text-forest/50 mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-amber font-semibold hover:underline"
            >
              {mode === "login" ? "Sign Up" : "Sign In"}
            </button>
          </p>
          )}
          
          {mode === "login" && (
            <p className="text-center text-sm text-forest/50 mt-2">
              Want to drive with us?{" "}
              <button
                data-testid="become-driver-link"
                onClick={() => setMode("driver_register")}
                className="text-forest font-semibold hover:underline"
              >
                Become a Driver
              </button>
            </p>
          )}

          {/* Policy links */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-forest/40">
              By continuing, you agree to our{" "}
              <a href="/policies/terms_of_service" className="text-forest/60 hover:text-forest underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/policies/privacy_policy" className="text-forest/60 hover:text-forest underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
